import React, { useState, useEffect, Suspense, lazy } from 'react';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { GameState, PlayerStats } from './types';
import { loadStats } from './services/statsService';
import InviteGateScreen from './components/screens/InviteGateScreen';
import { startSession, endSession } from './services/analyticsService';
import { autoRefreshToken } from './services/tokenService';

// Lazy load screen components for code splitting
const WelcomeScreen = lazy(() => import('./components/screens/WelcomeScreen'));
const GameScreen = lazy(() => import('./components/screens/GameScreen'));
const ProfileScreen = lazy(() => import('./components/screens/ProfileScreen'));
const WorkshopScreen = lazy(() => import('./components/screens/WorkshopScreen'));
const HelpModal = lazy(() => import('./components/ui/HelpModal'));
const AdminAuth = lazy(() => import('./components/admin/AdminAuth'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const HealthCheckPage = lazy(() => import('./components/admin/HealthCheckPage'));
const ComingSoon = lazy(() => import('./components/admin/ComingSoon'));

const App: React.FC = () => {
  // Check URL pathname for admin routes
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // If accessing admin routes, render admin interface
  if (currentPath.startsWith('/admin')) {
    return (
      <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center bg-gray-900"><div className="text-lg text-gray-300">Loading...</div></div>}>
        <AdminAuth>
          {currentPath === '/admin/health' ? (
            <HealthCheckPage />
          ) : currentPath === '/admin/analytics' ? (
            <ComingSoon
              title="Analytics"
              description="Advanced analytics and deep-dive metrics will be available here soon."
              features={[
                'Custom date range selection for historical data analysis',
                'User engagement trends and cohort analysis',
                'Argument quality metrics and debate patterns',
                'LLM judge performance analysis',
                'Game completion rates and drop-off points',
                'Real-time vs. historical data comparison',
              ]}
            />
          ) : currentPath === '/admin/reports' ? (
            <ComingSoon
              title="Reports"
              description="Automated investor and stakeholder reports will be available here soon."
              features={[
                'Monthly summary reports with key metrics',
                'User growth and retention reports',
                'Financial performance summaries',
                'System performance and reliability reports',
                'Exportable PDF and CSV formats',
                'Scheduled report delivery via email',
              ]}
            />
          ) : (
            <AdminDashboard />
          )}
        </AdminAuth>
      </Suspense>
    );
  }

  // SECURITY FIX: Token migration logic
  // Clear old session tokens that don't have a corresponding userId
  // This handles the breaking change from inviteCode-as-userId to unique userId
  const [sessionEstablished, setSessionEstablished] = useState(() => {
    const hasToken = localStorage.getItem('crux_session_token') !== null;
    const hasUserId = localStorage.getItem('crux_user_id') !== null;

    // If we have a token but no userId, it's an old token - clear it
    if (hasToken && !hasUserId) {
      localStorage.removeItem('crux_session_token');
      return false;
    }

    return hasToken && hasUserId;
  });
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpInitialTab, setHelpInitialTab] = useState<'about' | 'quick' | 'full' | 'scoring' | 'fairness' | 'privacy' | 'students'>('quick');
  const [showProfile, setShowProfile] = useState(false);
  const [showWorkshop, setShowWorkshop] = useState(false);
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);

  // Auto-refresh token on mount and periodically
  useEffect(() => {
    // Check and refresh token immediately on app load
    if (sessionEstablished) {
      autoRefreshToken();
    }

    // Check token refresh every 1 hour
    const refreshInterval = setInterval(() => {
      if (sessionEstablished) {
        autoRefreshToken();
      }
    }, 60 * 60 * 1000); // 1 hour

    return () => clearInterval(refreshInterval);
  }, [sessionEstablished]);

  // Start analytics session on mount
  useEffect(() => {
    startSession();

    // End session on page unload
    const handleBeforeUnload = () => {
      endSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      endSession();
    };
  }, []);

  // Load stats on mount with checksum verification
  useEffect(() => {
    const initStats = async () => {
      const stats = await loadStats();
      setPlayerStats(stats);
    };
    initStats();
  }, []);

  const handleGameCreated = (newGameState: GameState) => {
    setGameState(newGameState);
    setShowProfile(false);
    setShowWorkshop(false);
  };

  const handleGameLeft = () => {
    setGameState(null);
  };

  const handleShowProfile = () => {
    setShowProfile(true);
  };

  const handleHideProfile = () => {
    setShowProfile(false);
  };

  const handleShowWorkshop = () => {
    setShowWorkshop(true);
    setShowProfile(false);
  };

  const handleHideWorkshop = () => {
    setShowWorkshop(false);
  };

  const handleSessionEstablished = () => {
    setSessionEstablished(true);
  };

  // Show invite gate if session not established
  if (!sessionEstablished) {
    return <InviteGateScreen onSessionEstablished={handleSessionEstablished} />;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-gray-900 text-gray-200">
        <header className="w-full bg-gray-900/80 backdrop-blur-sm p-4 shadow-lg flex-shrink-0 absolute top-0 z-20 border-b border-gray-700">
            <div className="max-w-7xl mx-auto flex justify-between items-center px-2 sm:px-0">
                <div className="flex items-baseline gap-2">
                    <h1 className="font-display text-3xl font-bold tracking-wider bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"><span className="text-4xl">C</span>RUX</h1>
                    <span className="text-sm text-gray-300">The Principles Debate</span>
                </div>
                <div className="flex gap-2 sm:gap-4 items-center">
                    {gameState && (
                        <button
                            onClick={handleGameLeft}
                            className="text-red-400 text-sm sm:text-base font-semibold hover:text-red-300 transition-colors"
                            aria-label="Leave Game"
                        >
                            ← Leave
                        </button>
                    )}
                    {!gameState && !showProfile && !showWorkshop && (
                        <>
                            <button
                                onClick={handleShowProfile}
                                className="text-purple-300 text-sm sm:text-base font-semibold hover:text-white transition-colors"
                                aria-label="View Profile"
                            >
                                👤 Profile
                            </button>
                            <button
                                onClick={handleShowWorkshop}
                                className="text-orange-300 text-sm sm:text-base font-semibold hover:text-white transition-colors"
                                aria-label="Argument Workshop"
                            >
                                🛠️ Workshop
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => {
                            setHelpInitialTab('about');
                            setIsHelpOpen(true);
                        }}
                        className="text-green-300 text-sm sm:text-base font-semibold hover:text-white transition-colors"
                        aria-label="About CRUX"
                    >
                        About
                    </button>
                    <button
                        onClick={() => {
                            setHelpInitialTab('quick');
                            setIsHelpOpen(true);
                        }}
                        className="text-blue-300 text-sm sm:text-base font-semibold hover:text-white transition-colors"
                        aria-label="How to Play"
                    >
                        Help ?
                    </button>
                </div>
            </div>
        </header>

        <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center"><div className="text-lg text-gray-300">Loading...</div></div>}>
            <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} initialTab={helpInitialTab} />
        </Suspense>

        <main className="flex-grow w-full flex flex-col items-center justify-center p-4 pt-20">
            <Suspense fallback={<div className="text-center text-lg text-gray-300"></div>}>
                {gameState ? (
                    <ErrorBoundary>
                        <GameScreen
                            initialGameState={gameState}
                            onGameLeft={handleGameLeft}
                        />
                    </ErrorBoundary>
                ) : showProfile && playerStats ? (
                    <ProfileScreen stats={playerStats} onBack={handleHideProfile} />
                ) : showProfile && !playerStats ? (
                    <div className="text-center text-gray-300">Loading stats...</div>
                ) : showWorkshop ? (
                    <WorkshopScreen onBackToMenu={handleHideWorkshop} />
                ) : (
                    <WelcomeScreen onGameStarted={handleGameCreated} />
                )}
            </Suspense>
        </main>
    </div>
  );
};

export default App;
